import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import PrimeBadge from "@/components/PrimeBadge";
import { STAGES } from "@/lib/config";
import {
  ArrowRight,
  Plus,
  Search,
  Rocket,
  DownloadCloud,
  MessageCircle,
  CreditCard,
  Settings,
  ListChecks,
  Clock,
  CheckCircle2,
  Circle,
  Bell,
  LayoutGrid,
  TrendingUp,
  ShieldCheck,
  FileVideo,
  FileText,
} from "lucide-react";

/* Open the receipt PDF in a new tab (auth header attached via api). */
async function viewReceipt(orderId) {
  try {
    const { toast } = await import("sonner");
    const { api } = await import("@/lib/api");
    const { data } = await api.get(`/orders/${orderId}/receipt.pdf`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const w = window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    if (!w) toast.error("Popup blocked — allow popups to view receipt.");
  } catch {
    const { toast } = await import("sonner");
    toast.error("Failed to load receipt");
  }
}

/* ---------------------------- helpers ---------------------------- */

function stagePct(stage) {
  const idx = Math.max(0, STAGES.indexOf(stage));
  return Math.round(((idx + 1) / STAGES.length) * 100);
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  const d = Math.ceil(diff / 86400000);
  return d;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

// Returns { title, subtitle, unitCount } for either old (package_clips)
// or new (services[]) order shapes.
function summarizeOrder(o) {
  if (Array.isArray(o.services) && o.services.length) {
    const primary = o.services[0];
    const total = o.services.reduce((s, x) => s + (x.quantity || 0), 0);
    const title =
      o.primary_service_name ||
      primary.service_id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const subtitle =
      o.services.length > 1
        ? `${total} pieces · ${o.services.length} services`
        : `${total} ${primary.service_id === "long_form" ? (total === 1 ? "video" : "videos") : "pieces"} · ${primary.style_id}`;
    return { title, subtitle, unitCount: total };
  }
  return {
    title: `${o.package_clips || 0} pieces`,
    subtitle: o.editing_style || "—",
    unitCount: o.package_clips || 0,
  };
}

/* Color per production stage — used on the dot inside <StageBadge/> so the
 * status feed reads at a glance. Delivered → green, Order Received → muted,
 * everything in-between escalates from cyan → indigo → blue → amber → orange. */
const STAGE_TONES = {
  "Order Received": "#8a8a90",
  "Content Downloaded": "#22d3ee",   // cyan — assets pulled
  "Editors Assigned": "#a5b4fc",     // indigo — team locked in
  "Editing In Progress": "#6589ff",  // blue — active work
  "Quality Check": "#fbbf24",        // amber — checking
  "Final Review": "#fb923c",         // orange — nearly out the door
  "Delivered": "#22c55e",            // green — done
};

function StageBadge({ stage }) {
  const delivered = stage === "Delivered";
  const dotColor = STAGE_TONES[stage] || "#8a8a90";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border whitespace-nowrap ${
        delivered
          ? "bg-white text-black border-white"
          : "bg-[#0d0d0d] text-white border-[#2b2b2b]"
      }`}
      data-testid="stage-badge"
    >
      {delivered ? (
        <CheckCircle2 size={11} />
      ) : (
        <span
          className="inline-block h-2 w-2 rounded-full animate-pulse shrink-0"
          style={{
            background: dotColor,
            boxShadow: `0 0 6px ${dotColor}`,
          }}
        />
      )}
      {stage}
    </span>
  );
}

/* ---------------------------- Quick actions ---------------------------- */

const QUICK_ACTIONS = [
  { key: "new", label: "Place a new order", desc: "Start a fresh clip request", Icon: Plus, to: "/order", accent: true },
  { key: "orders", label: "View all orders", desc: "Full history and filters", Icon: ListChecks, to: "#orders" },
  { key: "downloads", label: "Download files", desc: "Grab delivered clips", Icon: DownloadCloud, to: "#downloads" },
  { key: "support", label: "Message support", desc: "Chat with our team", Icon: MessageCircle, to: "/contact" },
  { key: "billing", label: "Manage billing", desc: "Invoices and payment", Icon: CreditCard, to: "#billing" },
  { key: "settings", label: "Account settings", desc: "Profile and security", Icon: Settings, to: "/settings" },
];

/* ---------------------------- Main page ---------------------------- */

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackId, setTrackId] = useState("");
  const [primeMe, setPrimeMe] = useState(null);

  useEffect(() => {
    api
      .get("/orders")
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
    api
      .get("/prime/me")
      .then((r) => setPrimeMe(r.data))
      .catch(() => {});
  }, []);

  const activeOrders = useMemo(
    () => orders.filter((o) => o.stage !== "Delivered"),
    [orders],
  );
  const deliveredOrders = useMemo(
    () => orders.filter((o) => o.stage === "Delivered"),
    [orders],
  );

  const stats = {
    active: activeOrders.length,
    delivered: deliveredOrders.length,
    clips: orders.reduce((sum, o) => {
      if (Array.isArray(o.services) && o.services.length) {
        return sum + o.services.reduce((s, x) => s + (x.quantity || 0), 0);
      }
      return sum + (o.package_clips || 0);
    }, 0),
    nextEta: activeOrders
      .map((o) => o.estimated_delivery)
      .filter(Boolean)
      .sort()[0],
  };

  // synthetic editor updates from orders
  const updates = useMemo(() => {
    const feed = [];
    activeOrders.slice(0, 6).forEach((o) => {
      const dLeft = daysUntil(o.estimated_delivery);
      feed.push({
        id: `u-${o.order_id}-stage`,
        title: `${o.stage} — Order #${o.order_id.slice(-6)}`,
        body:
          o.admin_notes ||
          {
            "Order Received": "Your order is queued. An editor will be assigned shortly.",
            "Content Downloaded": "We've pulled your source files and started prep.",
            "Editors Assigned": "Your editor is reviewing the brief and planning the edit.",
            "Editing In Progress": "Your editor is actively crafting your content right now.",
            "Quality Check": "QC pass in progress — polishing pacing and captions.",
            "Final Review": "Head editor is doing the final review before delivery.",
            Delivered: "Deliverables are ready to download.",
          }[o.stage],
        eta:
          dLeft != null
            ? dLeft <= 0
              ? "Any moment now"
              : `Expected in ${dLeft} day${dLeft === 1 ? "" : "s"}`
            : null,
      });
    });
    return feed;
  }, [activeOrders]);

  const firstName = user?.name?.split(" ")[0] || "creator";

  function handleTrack(e) {
    e.preventDefault();
    // Accept "#a4c9f2", "a4c9f2", or the full order id. Strip any leading '#'
    // and normalize whitespace so users can paste however they like.
    const id = trackId.trim().replace(/^#+/, "");
    if (!id) return;
    const match = orders.find(
      (o) => o.order_id === id || o.order_id.slice(-6).toLowerCase() === id.toLowerCase(),
    );
    if (match) navigate(`/dashboard/order/${match.order_id}`);
    else navigate(`/dashboard/order/${id}`);
  }

  const isEmpty = !loading && orders.length === 0;

  // Theme tokens — kept minimal & centralized so light/dark render intentionally.
  const T = {
    pageBg: isLight ? "#f7f7f8" : "#000000",
    text: isLight ? "#0a0a0a" : "#ffffff",
    textMuted: isLight ? "#6a6a70" : "#a0a0a0",
    cardBg: isLight ? "#ffffff" : "#0d0d0d",
    cardBorder: isLight ? "#e5e5ea" : "#1f1f22",
    cardInset: isLight ? "inset 0 1px 0 rgba(255,255,255,0.9)" : "inset 0 1px 0 rgba(255,255,255,0.03)",
    inputBg: isLight ? "#ffffff" : "#0a0a0a",
    inputBorder: isLight ? "#e5e5ea" : "#2b2b2b",
    subtleBtnBorder: isLight ? "#e5e5ea" : "#2b2b2b",
    subtleBtnHover: isLight ? "#f5f5f7" : "#111",
  };
  const ACCENT = "#6589ff"; // Bliencer app-level blue
  const ACCENT_SOFT = "rgba(101,137,255,0.14)";
  const ACCENT_BORDER = "rgba(101,137,255,0.32)";
  // Blue gradient shared by all primary CTAs on dashboard
  const BLUE_GRAD = "linear-gradient(120deg, #8ab0ff 0%, #6589ff 45%, #4a6fee 100%)";
  const BLUE_SHADOW =
    "0 0 0 1px rgba(255,255,255,0.22) inset, 0 2px 10px -4px rgba(101,137,255,0.32), 0 0 14px -6px rgba(74,111,238,0.28)";

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: T.pageBg, color: T.text }}
      data-testid="dashboard-root"
    >
      {/* Ambient blue orbital glow — subtle wash, no visible ring */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-48 right-0 sm:right-10 w-[820px] h-[520px]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(101,137,255,0.22) 0%, rgba(101,137,255,0.06) 45%, transparent 75%)",
          filter: "blur(50px)",
          transform: "rotate(-12deg)",
          opacity: isLight ? 0.55 : 1,
        }}
      />

      <Nav />

      {/* ---------------- Header ---------------- */}
      <section className="pt-28 pb-6 px-6 sm:px-10 relative">
        <div className="max-w-7xl mx-auto flex items-end justify-between flex-wrap gap-6">
          <div>
            <div
              className="text-[11px] uppercase tracking-[0.22em] mb-3 flex items-center gap-2 font-semibold"
              style={{ color: ACCENT }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ background: ACCENT, boxShadow: `0 0 10px ${ACCENT}` }}
              />
              Client portal
            </div>
            <h1
              className="text-5xl sm:text-6xl font-bold tracking-tighter"
              data-testid="dashboard-title"
              style={{ letterSpacing: "-0.035em", color: T.text }}
            >
              Welcome back, {firstName}.
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed" style={{ color: T.textMuted }}>
              {isEmpty
                ? "Let's get your first order rolling. Your dashboard will fill up here."
                : stats.active > 0
                  ? `You have ${stats.active} active order${stats.active === 1 ? "" : "s"} in progress. We'll keep you posted.`
                  : "No orders in progress right now — ready for another round?"}
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/order"
              data-testid="dashboard-new-order"
              className="blue-cta rounded-full pl-4 pr-5 h-11 text-sm font-semibold btn-anim inline-flex items-center gap-2"
              style={{
                background: BLUE_GRAD,
                color: "#0b1633",
                boxShadow: BLUE_SHADOW,
              }}
            >
              <Plus size={15} strokeWidth={2.5} /> New order
            </Link>
            <Link
              to="/affiliate"
              data-testid="dashboard-affiliate"
              className="rounded-full px-4 h-11 text-sm btn-anim inline-flex items-center gap-2"
              style={{
                border: `1px solid ${T.subtleBtnBorder}`,
                color: T.text,
                background: "transparent",
              }}
            >
              <TrendingUp size={14} /> Affiliate
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- Stat strip ---------------- */}
      <section className="px-6 sm:px-10 relative">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              l: "Active",
              v: stats.active,
              sub: `Order${stats.active === 1 ? "" : "s"} in progress`,
              Icon: Clock,
              tone: "blue",
              to: "#orders",
            },
            {
              l: "Delivered",
              v: stats.delivered,
              sub: `Order${stats.delivered === 1 ? "" : "s"} completed`,
              Icon: CheckCircle2,
              tone: "green",
              to: "#downloads",
            },
            {
              l: "Total content",
              v: stats.clips,
              sub: "Pieces created",
              Icon: FileVideo,
              tone: "indigo",
              to: "#orders",
            },
            {
              l: "Next delivery",
              v: stats.nextEta ? formatDate(stats.nextEta) : "—",
              sub: stats.nextEta ? "On the way" : "Nothing pending",
              Icon: Rocket,
              tone: "pink",
              to: "#orders",
            },
          ].map(({ l, v, sub, Icon, tone, to }) => (
            <StatCard
              key={l}
              label={l}
              value={v}
              sub={sub}
              Icon={Icon}
              tone={tone}
              to={to}
              tokens={T}
              testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`}
            />
          ))}
        </div>
      </section>

      {/* ---------------- Active orders (surfaced first) ---------------- */}
      <section id="orders" className="px-6 sm:px-10 mt-14 relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div
                className="text-[11px] uppercase tracking-[0.22em] mb-2 flex items-center gap-2 font-semibold"
                style={{ color: ACCENT }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }}
                />
                Active work
              </div>
              <h2
                className="text-3xl font-bold tracking-tighter"
                style={{ letterSpacing: "-0.02em", color: T.text }}
              >
                Currently in progress
              </h2>
            </div>
            {activeOrders.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="text-xs" style={{ color: T.textMuted }}>
                  {activeOrders.length} order{activeOrders.length === 1 ? "" : "s"}
                </div>
                <a
                  href="#orders"
                  className="text-xs btn-anim inline-flex items-center gap-1 font-semibold"
                  style={{ color: ACCENT }}
                >
                  View all <ArrowRight size={12} strokeWidth={2.5} />
                </a>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-sm" style={{ color: T.textMuted }}>Loading your orders…</div>
          ) : isEmpty ? (
            <OnboardingEmpty />
          ) : activeOrders.length === 0 ? (
            <div className="squircle border border-[#2b2b2b] bg-[#0d0d0d] p-10 text-center">
              <CheckCircle2 size={24} className="mx-auto text-[#666]" />
              <div className="mt-3 text-lg">All caught up.</div>
              <div className="text-[#a0a0a0] text-sm mt-1">
                No active orders right now. Ready for another round?
              </div>
              <Link
                to="/order"
                data-testid="cta-new-when-idle"
                className="btn-accent mt-6 inline-flex items-center gap-2 squircle px-5 py-2.5 font-medium btn-anim"
              >
                Start a new order <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {activeOrders.map((o, i) => {
                const pct = stagePct(o.stage);
                const dLeft = daysUntil(o.estimated_delivery);
                return (
                  <motion.div
                    key={o.order_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="squircle border border-[#2b2b2b] bg-[#111] p-6 flex flex-col"
                    data-testid={`active-order-${o.order_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0">
                        <div className="text-xs text-[#a0a0a0]">
                          Order #{o.order_id.slice(-6)}
                        </div>
                        <div className="font-semibold text-lg mt-1 tracking-tight">
                          {summarizeOrder(o).title}
                        </div>
                        <div className="text-xs text-[#a0a0a0] mt-0.5">
                          {summarizeOrder(o).subtitle}
                        </div>
                      </div>
                      <div className="self-start">
                        <StageBadge stage={o.stage} />
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-[#a0a0a0]">
                          Step {Math.min(STAGES.indexOf(o.stage) + 1, STAGES.length)} of {STAGES.length}
                          <span className="text-white ml-2">· {o.stage}</span>
                        </span>
                        <span className="text-white font-medium">{pct}%</span>
                      </div>
                      <div className="progress-track">
                        <motion.div
                          className="progress-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </div>
                    </div>

                    <div className="mt-5 flex items-center justify-between text-xs text-[#a0a0a0]">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} />
                        {dLeft != null && dLeft > 0
                          ? `~${dLeft} day${dLeft === 1 ? "" : "s"} left`
                          : dLeft != null && dLeft <= 0
                            ? "Wrapping up now"
                            : "ETA pending"}
                      </span>
                      <span>Est. {formatDate(o.estimated_delivery)}</span>
                    </div>

                    <Link
                      data-testid={`active-order-view-${o.order_id}`}
                      to={`/dashboard/order/${o.order_id}`}
                      className="mt-6 squircle-sm border border-[#2b2b2b] px-4 py-2.5 text-sm hover:bg-[#0d0d0d] btn-anim inline-flex items-center justify-center gap-2"
                    >
                      Open order <ArrowRight size={14} />
                    </Link>
                    <button
                      onClick={() => viewReceipt(o.order_id)}
                      data-testid={`dashboard-receipt-${o.order_id}`}
                      className="mt-2 text-xs text-[#a0a0a0] hover:text-white btn-anim inline-flex items-center justify-center gap-1.5"
                    >
                      <FileText size={12} /> View receipt (PDF)
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ---------------- Track order (moved below Active orders per spec) ---------------- */}
      <section className="px-6 sm:px-10 mt-12 relative">
        <div className="max-w-7xl mx-auto">
          <div
            className="rounded-3xl p-6 sm:p-8 relative overflow-hidden"
            data-testid="track-order-card"
            style={{
              background: isLight
                ? "linear-gradient(180deg, #f2f6ff 0%, #ffffff 55%)"
                : "linear-gradient(180deg, rgba(28,48,120,0.22) 0%, rgba(15,20,40,0.35) 55%, rgba(13,13,13,0.85) 100%)",
              border: `1px solid ${isLight ? "rgba(101,137,255,0.22)" : "rgba(101,137,255,0.22)"}`,
              boxShadow: isLight
                ? "0 24px 60px -30px rgba(101,137,255,0.28)"
                : "inset 0 1px 0 rgba(255,255,255,0.04), 0 24px 60px -30px rgba(101,137,255,0.35)",
            }}
          >
            {/* Ambient blue wash */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(700px 220px at 0% 0%, rgba(101,137,255,0.14), transparent 60%)",
              }}
            />
            <div className="relative flex items-start justify-between flex-wrap gap-6">
              <div className="max-w-md">
                <div
                  className="text-[11px] uppercase tracking-[0.22em] mb-3 flex items-center gap-2 font-semibold"
                  style={{ color: ACCENT }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }}
                  />
                  Track your order
                </div>
                <h2
                  className="text-3xl font-bold tracking-tighter"
                  style={{ letterSpacing: "-0.02em", color: T.text }}
                >
                  Where is my order?
                </h2>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: T.textMuted }}>
                  Paste an order number or the last 6 characters to jump straight to
                  its live progress page.
                </p>
              </div>
              <form
                onSubmit={handleTrack}
                className="flex items-center gap-2 w-full sm:w-auto"
                data-testid="track-order-form"
              >
                <div className="relative flex-1 sm:flex-none">
                  <Search
                    size={14}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2"
                    style={{ color: T.textMuted }}
                  />
                  <input
                    data-testid="track-order-input"
                    value={trackId}
                    onChange={(e) => setTrackId(e.target.value)}
                    placeholder="e.g. a4c9f2"
                    className="w-full sm:w-72 pl-10 pr-4 h-11 rounded-full text-sm focus:outline-none btn-anim"
                    style={{
                      background: T.inputBg,
                      border: `1px solid ${T.inputBorder}`,
                      color: T.text,
                    }}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="track-order-submit"
                  className="blue-cta rounded-full px-5 h-11 text-sm font-semibold btn-anim inline-flex items-center gap-2"
                  style={{
                    background: BLUE_GRAD,
                    color: "#0b1633",
                    boxShadow: BLUE_SHADOW,
                  }}
                >
                  Track <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Quick actions ---------------- */}
      <section className="px-6 sm:px-10 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-1">
                Shortcuts
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">Quick actions</h2>
            </div>
            <LayoutGrid size={14} className="text-[#666]" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {QUICK_ACTIONS.map((a) => {
              const Icon = a.Icon;
              const isInternal = a.to.startsWith("/");
              const Wrapper = isInternal ? Link : "a";
              const wrapperProps = isInternal ? { to: a.to } : { href: a.to };
              return (
                <Wrapper
                  key={a.key}
                  data-testid={`quick-action-${a.key}`}
                  {...wrapperProps}
                  className={`group squircle p-5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 btn-anim border relative ${
                    a.accent
                      ? "btn-accent border-transparent"
                      : "bg-[#0d0d0d] border-[#2b2b2b] hover:bg-[#111] hover:border-[#3a3a3a]"
                  }`}
                >
                  <div
                    className={`h-10 w-10 squircle-sm flex items-center justify-center shrink-0 ${
                      a.accent
                        ? "bg-white/15 border border-white/20 text-white"
                        : "bg-[#111] border border-[#2b2b2b]"
                    }`}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[15px] tracking-tight leading-tight">
                      {a.label}
                    </div>
                    <div
                      className={`text-xs mt-1 leading-snug ${
                        a.accent ? "text-white/75" : "text-[#a0a0a0]"
                      }`}
                    >
                      {a.desc}
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className={`absolute sm:static top-4 right-4 sm:ml-auto sm:mt-1 transition-transform group-hover:translate-x-0.5 ${
                      a.accent ? "text-white" : "text-[#666]"
                    }`}
                  />
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- Updates + Subscription ---------------- */}
      <section className="px-6 sm:px-10 mt-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-5">
          {/* Editor updates */}
          <div
            className="lg:col-span-2 squircle border border-[#2b2b2b] bg-[#0d0d0d] p-6 sm:p-8"
            data-testid="editor-updates-card"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Bell size={14} className="text-[#a0a0a0]" />
                <h3 className="text-lg font-semibold tracking-tight">
                  Updates from your editors
                </h3>
              </div>
              <span className="text-[11px] text-[#a0a0a0]">Live</span>
            </div>

            {updates.length === 0 ? (
              <div className="text-sm text-[#a0a0a0]">
                No live updates. New activity will appear here as your editors work.
              </div>
            ) : (
              <ul className="space-y-4">
                {updates.map((u, i) => (
                  <motion.li
                    key={u.id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex gap-4"
                  >
                    <div className="mt-1 h-2 w-2 rounded-full bg-white shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{u.title}</div>
                      <div className="text-sm text-[#a0a0a0] mt-1 leading-relaxed">
                        {u.body}
                      </div>
                      {u.eta && (
                        <div className="text-[11px] text-[#7a7a7a] uppercase tracking-widest mt-2">
                          {u.eta}
                        </div>
                      )}
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>

          {/* Bliencer Prime membership */}
          {primeMe?.status === "active" ? (
            <div
              className="squircle border border-[#2b2b2b] bg-[#111] p-6 sm:p-8 flex flex-col"
              data-testid="subscription-card"
              style={{
                background:
                  "linear-gradient(180deg, rgba(168,85,247,0.10) 0%, rgba(168,85,247,0.02) 100%)",
                borderColor: "rgba(168,85,247,0.35)",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0]">
                  Membership
                </div>
                <PrimeBadge plan={primeMe.plan} size="md" />
              </div>
              <div className="text-2xl font-semibold tracking-tight mt-2">
                {primeMe.plan === "prime_pro" ? "Bliencer Pro Plus" : "Bliencer Pro"}
              </div>
              <p className="text-sm text-[#a0a0a0] mt-2">
                Renews {new Date(primeMe.renewal_date).toLocaleDateString()} · billed monthly
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0a0a0]">
                    Discount
                  </div>
                  <div className="text-xl font-semibold mt-1" style={{ color: "#a855f7" }}>
                    {primeMe.discount_percentage}%
                  </div>
                </div>
                <div className="squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0a0a0]">
                    Lifetime saved
                  </div>
                  <div className="text-xl font-semibold mt-1">
                    ${Number(primeMe.lifetime_savings || 0).toFixed(0)}
                  </div>
                </div>
              </div>

              {primeMe.plan === "prime_pro" && (
                <div className="mt-3 squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0a0a0]">
                    Free thumbnail credits
                  </div>
                  <div className="text-xl font-semibold mt-1" style={{ color: "#22c55e" }}>
                    {primeMe.thumbnail_credits} available this cycle
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-2">
                <Link
                  to="/prime"
                  data-testid="upgrade-cta"
                  className="squircle-sm border border-[#2b2b2b] px-5 py-3 text-sm font-medium hover:bg-[#0d0d0d] btn-anim inline-flex items-center justify-center gap-2"
                >
                  Manage membership <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ) : (
            <div
              className="squircle border border-[#2b2b2b] bg-[#111] p-6 sm:p-8 flex flex-col"
              data-testid="subscription-card"
            >
              <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-2">
                Join Bliencer Pro
              </div>
              <div className="text-2xl font-semibold tracking-tight">
                Save 15–25% on every order
              </div>
              <p className="text-sm text-[#a0a0a0] mt-2">
                Priority queue, unlimited revisions, faster turnaround, and a monthly
                strategy call — from $49/month.
              </p>

              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Automatic discount on every order",
                  "Priority editor queue",
                  "Unlimited revisions",
                  "Dedicated support",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <ShieldCheck size={14} style={{ color: "#a855f7" }} /> {f}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-col gap-2">
                <Link
                  to="/prime"
                  data-testid="upgrade-cta"
                  className="squircle-sm px-5 py-3 text-sm font-medium btn-anim inline-flex items-center justify-center gap-2 text-white"
                  style={{ background: "#a855f7" }}
                >
                  Explore Prime <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="manage-billing-cta"
                  className="squircle-sm border border-[#2b2b2b] px-5 py-3 text-sm font-medium hover:bg-[#0d0d0d] btn-anim inline-flex items-center justify-center gap-2"
                >
                  Manage billing
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ---------------- Order history ---------------- */}
      <section id="downloads" className="px-6 sm:px-10 mt-12 mb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-1">
                History
              </div>
              <h2 className="text-2xl font-semibold tracking-tight">Delivered orders</h2>
            </div>
            {deliveredOrders.length > 0 && (
              <div className="text-xs text-[#a0a0a0]">
                {deliveredOrders.length} completed
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-[#a0a0a0] text-sm">Loading…</div>
          ) : deliveredOrders.length === 0 ? (
            <div className="squircle border border-[#2b2b2b] bg-[#0d0d0d] p-8 text-sm text-[#a0a0a0]">
              Delivered orders and downloadable files will appear here.
            </div>
          ) : (
            <div className="squircle border border-[#2b2b2b] bg-[#0d0d0d] overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-3 text-[11px] uppercase tracking-widest text-[#a0a0a0] border-b border-[#181818]">
                <div>Order</div>
                <div className="hidden sm:block">Delivered</div>
                <div className="hidden md:block">Files</div>
                <div className="text-right">Action</div>
              </div>
              {deliveredOrders.map((o) => (
                <div
                  key={o.order_id}
                  className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-4 items-center border-b border-[#181818] last:border-b-0"
                  data-testid={`history-order-${o.order_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {summarizeOrder(o).title}
                    </div>
                    <div className="text-xs text-[#a0a0a0]">
                      #{o.order_id.slice(-6)}
                    </div>
                  </div>
                  <div className="hidden sm:block text-sm text-[#a0a0a0]">
                    {formatDate(o.estimated_delivery)}
                  </div>
                  <div className="hidden md:block text-sm text-[#a0a0a0]">
                    {(o.deliverables?.length || 0)} file
                    {(o.deliverables?.length || 0) === 1 ? "" : "s"}
                  </div>
                  <Link
                    to={`/dashboard/order/${o.order_id}`}
                    data-testid={`history-view-${o.order_id}`}
                    className="squircle-sm border border-[#2b2b2b] px-4 py-2 text-xs hover:bg-[#111] btn-anim inline-flex items-center gap-2 justify-self-end"
                  >
                    <DownloadCloud size={12} /> View
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ---------------------------- Onboarding empty state ---------------------------- */

function OnboardingEmpty() {
  return (
    <div
      className="squircle-lg border border-[#2b2b2b] bg-[#0d0d0d] p-10 sm:p-14 relative overflow-hidden"
      data-testid="onboarding-empty"
    >
      <div className="max-w-2xl">
        <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-3">
          Get started
        </div>
        <h3 className="text-3xl sm:text-4xl font-bold tracking-tighter">
          Place your first order
        </h3>
        <p className="mt-4 text-[#a0a0a0] leading-relaxed max-w-lg">
          Share a link to your raw content, pick an editing style, and choose a
          package. Real editors will handle the rest — you'll track every step from
          here.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          {[
            { t: "1 · Share content", d: "YouTube, Drive, Loom, any URL" },
            { t: "2 · Pick a style", d: "Hormozi, Podcast, Luxury +7 more" },
            { t: "3 · Track progress", d: "Live editor updates here" },
          ].map((s) => (
            <div key={s.t} className="squircle-sm border border-[#242424] bg-black p-4">
              <div className="text-xs uppercase tracking-widest text-[#a0a0a0]">
                {s.t}
              </div>
              <div className="text-sm mt-2">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/order"
            data-testid="onboarding-cta"
            className="btn-accent squircle px-6 py-3.5 font-medium btn-anim inline-flex items-center gap-2"
          >
            Place your first order <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-testid="onboarding-contact"
            className="squircle border border-[#2b2b2b] px-6 py-3.5 font-medium hover:bg-[#111] btn-anim inline-flex items-center gap-2"
          >
            <MessageCircle size={14} /> Talk to us first
          </Link>
        </div>
      </div>
    </div>
  );
}


/* ----------------------- StatCard ----------------------- */
/*
 * Section-colored gradient stat card — mirrors the "Track your order" card
 * language. Each tone carries its own subtle vertical gradient background,
 * ambient radial wash, chip color and label color. Light mode uses a softer
 * pastel version of the same recipe so contrast stays clean on white.
 */
const STAT_TONES = {
  blue: {
    label: "#8ab0ff",
    icon: "#8ab0ff",
    chipBg: "rgba(101,137,255,0.14)",
    chipBorder: "rgba(101,137,255,0.32)",
    cardGradient:
      "linear-gradient(180deg, rgba(28,48,120,0.08) 0%, rgba(13,13,13,0.85) 100%)",
    cardBorder: "rgba(101,137,255,0.10)",
    cardShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(101,137,255,0.05), transparent 60%)",
    light: {
      label: "#3757d1",
      icon: "#4a6fee",
      chipBg: "rgba(101,137,255,0.10)",
      chipBorder: "rgba(101,137,255,0.35)",
      cardGradient: "linear-gradient(180deg, #fafbff 0%, #ffffff 65%)",
      cardBorder: "rgba(101,137,255,0.12)",
      cardShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
      radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(101,137,255,0.04), transparent 60%)",
    },
  },
  green: {
    label: "#4ade80",
    icon: "#4ade80",
    chipBg: "rgba(34,197,94,0.12)",
    chipBorder: "rgba(34,197,94,0.30)",
    cardGradient:
      "linear-gradient(180deg, rgba(20,83,45,0.08) 0%, rgba(13,13,13,0.85) 100%)",
    cardBorder: "rgba(34,197,94,0.10)",
    cardShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(34,197,94,0.05), transparent 60%)",
    light: {
      label: "#15803d",
      icon: "#16a34a",
      chipBg: "rgba(34,197,94,0.10)",
      chipBorder: "rgba(34,197,94,0.35)",
      cardGradient: "linear-gradient(180deg, #f7fdf9 0%, #ffffff 65%)",
      cardBorder: "rgba(34,197,94,0.12)",
      cardShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
      radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(34,197,94,0.04), transparent 60%)",
    },
  },
  indigo: {
    label: "#a5b4fc",
    icon: "#a5b4fc",
    chipBg: "rgba(129,140,248,0.14)",
    chipBorder: "rgba(129,140,248,0.32)",
    cardGradient:
      "linear-gradient(180deg, rgba(49,46,129,0.09) 0%, rgba(13,13,13,0.85) 100%)",
    cardBorder: "rgba(129,140,248,0.10)",
    cardShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(129,140,248,0.05), transparent 60%)",
    light: {
      label: "#4338ca",
      icon: "#4f46e5",
      chipBg: "rgba(129,140,248,0.10)",
      chipBorder: "rgba(129,140,248,0.35)",
      cardGradient: "linear-gradient(180deg, #f9f9ff 0%, #ffffff 65%)",
      cardBorder: "rgba(129,140,248,0.12)",
      cardShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
      radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(129,140,248,0.04), transparent 60%)",
    },
  },
  pink: {
    label: "#f0abfc",
    icon: "#f0abfc",
    chipBg: "rgba(232,121,249,0.12)",
    chipBorder: "rgba(232,121,249,0.30)",
    cardGradient:
      "linear-gradient(180deg, rgba(112,26,117,0.08) 0%, rgba(13,13,13,0.85) 100%)",
    cardBorder: "rgba(232,121,249,0.10)",
    cardShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(232,121,249,0.05), transparent 60%)",
    light: {
      label: "#a21caf",
      icon: "#c026d3",
      chipBg: "rgba(232,121,249,0.10)",
      chipBorder: "rgba(232,121,249,0.35)",
      cardGradient: "linear-gradient(180deg, #fdf8ff 0%, #ffffff 65%)",
      cardBorder: "rgba(232,121,249,0.12)",
      cardShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
      radialWash: "radial-gradient(500px 200px at 0% 0%, rgba(232,121,249,0.04), transparent 60%)",
    },
  },
};

function StatCard({ label, value, sub, Icon, tone = "blue", tokens, to, onClick, testid }) {
  const base = STAT_TONES[tone] || STAT_TONES.blue;
  const isLight = tokens.text === "#0a0a0a";
  const T = isLight ? { ...base, ...base.light } : base;

  const handleClick = () => {
    if (onClick) return onClick();
    if (to && to.startsWith("#")) {
      const el = document.querySelector(to);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isInteractive = Boolean(to || onClick);
  const Cmp = isInteractive ? "button" : "div";

  return (
    <Cmp
      onClick={isInteractive ? handleClick : undefined}
      data-testid={testid}
      className={`stat-card relative rounded-2xl p-5 overflow-hidden text-left w-full ${
        isInteractive ? "cursor-pointer" : ""
      }`}
      style={{
        background: T.cardGradient,
        border: `1px solid ${T.cardBorder}`,
        boxShadow: T.cardShadow,
      }}
    >
      {/* Ambient corner wash — mirrors the Track your order card */}
      <div
        aria-hidden
        className="stat-card__wash absolute inset-0 pointer-events-none"
        style={{ background: T.radialWash }}
      />
      <div className="relative flex items-start gap-3">
        <span
          className="h-9 w-9 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: T.chipBg, border: `1px solid ${T.chipBorder}` }}
        >
          <Icon size={16} color={T.icon} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div
            className="text-[10.5px] uppercase tracking-[0.22em] font-bold"
            style={{ color: T.label }}
          >
            {label}
          </div>
          <div
            className="text-[32px] font-bold tracking-tighter mt-1.5 leading-none"
            style={{ letterSpacing: "-0.03em", color: tokens.text }}
          >
            {value}
          </div>
          <div className="text-[11.5px] mt-2" style={{ color: tokens.textMuted }}>{sub}</div>
        </div>
        {isInteractive && (
          <ArrowRight
            size={14}
            strokeWidth={2}
            className="stat-card__arrow ml-auto flex-shrink-0 opacity-40"
            style={{ color: T.label, transition: "transform 220ms ease, opacity 220ms ease" }}
          />
        )}
      </div>
    </Cmp>
  );
}

